import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { get, post } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import Unauthorized from "../../../Components/Unauthorized/Unauthorized";
import AddIcon from "@mui/icons-material/Add";
import AddSocial from "./AddSocial";
import PaginationBox from "../../../Components/Masters/Pagination";
import NoDataFound from "../../../Components/Masters/NoDataFound";
import { useUser } from "../../../context/AuthContext";
import MessagePopup from "../../../Components/Masters/MessagePopup";
import SelectField from "../../../Components/Masters/SelectField";
import { monthlist } from "../../../utils/constant";

const SocialData = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEntryTypeModalOpen, setIsEntryTypeModalOpen] = useState(false);
  // entryType controls whether user is creating annual or monthly data
  const [entryType, setEntryType] = useState("annual");
  const [entryYear, setEntryYear] = useState("");
  const [entryMonth, setEntryMonth] = useState("");
  const [entryErrors, setEntryErrors] = useState({
    year: "",
    month: "",
    copyYear: "",
    copyMonth: "",
  });
  const [copyPreviousChoice, setCopyPreviousChoice] = useState("no");
  const [copySourceYear, setCopySourceYear] = useState("");
  const [copySourceMonth, setCopySourceMonth] = useState("");
  const [previousCompletedEntries, setPreviousCompletedEntries] = useState([]);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = [2021, 2022, 2023, 2024, 2025, 2026];

  const monthlyOptions = useMemo(() => {
    const selectedYear = Number(entryYear);
    if (!selectedYear || selectedYear < currentYear) {
      return monthlist;
    }
    if (selectedYear > currentYear) {
      return [];
    }
    return monthlist.filter((month) => Number(month.id) <= currentMonth);
  }, [entryYear, currentYear, currentMonth]);

  const monthlyYearOptions = useMemo(() => {
    const selectedMonth = Number(entryMonth);
    // If user picks a future month, current year would be invalid for that month.
    if (selectedMonth && selectedMonth > currentMonth) {
      return years.filter((year) => Number(year) < currentYear);
    }
    return years;
  }, [entryMonth, currentMonth, years, currentYear]);

  const previousOptions = useMemo(() => {
    const entries = Array.isArray(previousCompletedEntries)
      ? previousCompletedEntries
      : Array.isArray(previousCompletedEntries?.data)
        ? previousCompletedEntries.data
        : [];

    return entries
      .filter(
        (item) =>
          (item?.status || "").toLowerCase() === "completed" &&
          (item?._socialDataRef?.module || item?.module || "").toLowerCase() ===
            entryType,
      )
      .map((item) => {
        const ref = item?._socialDataRef || {};
        return {
          id: item?._id,
          year: Number(ref?.yearToSubmit ?? ref?.yearToSbmit),
          month: Number(ref?.monthToSubmit ?? ref?.monthToSbmit),
        };
      })
      .filter((item) => item.year);
  }, [previousCompletedEntries, entryType]);

  const copyYearOptions = useMemo(() => {
    const yearsSet = new Set(
      previousOptions.map((item) => item.year).filter(Boolean),
    );
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [previousOptions]);

  const hasModulePreviousData = useMemo(
    () => previousOptions.length > 0,
    [previousOptions],
  );

  const copyMonthOptions = useMemo(() => {
    if (entryType !== "monthly") return [];
    const selectedCopyYear = Number(copySourceYear);
    const months = previousOptions
      .filter(
        (item) =>
          item.month &&
          (!selectedCopyYear || Number(item.year) === selectedCopyYear),
      )
      .map((item) => Number(item.month));
    const uniqueMonths = Array.from(new Set(months)).sort((a, b) => a - b);
    return monthlist.filter((m) => uniqueMonths.includes(Number(m.id)));
  }, [previousOptions, copySourceYear, entryType]);

  const selectedSourceId = useMemo(() => {
    if (copyPreviousChoice !== "yes") return "";
    if (entryType === "annual") {
      const match = previousOptions.find(
        (item) => item.year === Number(copySourceYear),
      );
      return match?.id || "";
    }

    const match = previousOptions.find(
      (item) =>
        item.year === Number(copySourceYear) &&
        item.month === Number(copySourceMonth),
    );
    return match?.id || "";
  }, [
    copyPreviousChoice,
    entryType,
    previousOptions,
    copySourceYear,
    copySourceMonth,
  ]);

  const canEntryTypeProceed = useMemo(() => {
    if (!entryYear) return false;
    if (entryType === "monthly" && !entryMonth) return false;
    if (copyPreviousChoice === "yes") {
      if (!copySourceYear) return false;
      if (entryType === "monthly" && !copySourceMonth) return false;
      if (!selectedSourceId) return false;
    }
    return true;
  }, [
    entryYear,
    entryType,
    entryMonth,
    copyPreviousChoice,
    copySourceYear,
    copySourceMonth,
    selectedSourceId,
  ]);

  const fetchData = useCallback(
    async (type) => {
      setIsLoading(true);
      try {
        const typeParam = Array.isArray(type) ? type.join(",") : type;
        const response = await get(
          `/social/getSocial?type=${typeParam}&page=${page}&companyId=${user?._id}`,
        );
        setData(response.data.data || []);
        setTotalPages(response.data.totalPages);
        setPage(response.data.currentPage);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [page, user?._id],
  );

  useEffect(() => {
    const type =
      activeTab === 0 ? ["Pending", "Partially Completed"] : "Completed";
    fetchData(type);
  }, [activeTab, fetchData]);

  const tabLabels = ["Pending", "Completed"];
  const tableHeaders = ["S. No", "Module", "Create Date", "Status", "Action"];

  const getMonthLabel = (monthNumber) => {
    const m = Number(monthNumber);
    if (!m || Number.isNaN(m) || m < 1 || m > 12) return "-";
    const match = monthlist?.find((x) => x.id === m);
    return match?.month || "-";
  };

  const getModuleDisplay = (row) => {
    const module = (row?.module || "").toString().toLowerCase();
    const year = row?._socialDataRef?.yearToSubmit;
    const month = row?._socialDataRef?.monthToSubmit;

    if (module === "monthly") {
      const monthNum = Number(month);
      if (year && monthNum >= 1 && monthNum <= 12) {
        // Get month abbreviation from monthlist
        const monthMatch = monthlist?.find((x) => x.id === monthNum);
        const monthAbbr = monthMatch?.month || "-";
        // Get last 2 digits of year
        const yearShort = year.toString().slice(-2);
        return `${module.charAt(0).toUpperCase() + module.slice(1)} ${monthAbbr}-${yearShort}`;
      }
      return year
        ? `${module.charAt(0).toUpperCase() + module.slice(1)} ${year}`
        : `${module.charAt(0).toUpperCase() + module.slice(1)} (-)`;
    } else if (module === "annual") {
      return year
        ? `${module.charAt(0).toUpperCase() + module.slice(1)} ${year}`
        : `${module.charAt(0).toUpperCase() + module.slice(1)} (-)`;
    }
    return row.module || "-";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "red";
      case "Partially Completed":
        return "orange";
      default:
        return "green";
    }
  };

  const getActionLabel = (status) => {
    switch (status) {
      case "Pending":
        return "Start";
      case "Partially Complete":
        return "Continue";
      default:
        return "View/Edit";
    }
  };

  const tabStyles = (isActive) => ({
    borderRadius: "4px",
    textTransform: "none",
    backgroundColor: isActive ? "#1C486B" : "transparent",
    color: isActive ? "white" : "#1C486B",
    border: isActive ? "none" : "1px solid #1C486B",
    "&:hover": {
      backgroundColor: isActive ? "#1C486B" : "#ededed",
    },
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleAddNewClick = () => {
    // Reset selection each time dialog is opened
    setEntryType("annual");
    setEntryYear("");
    setEntryMonth("");
    setCopyPreviousChoice("no");
    setCopySourceYear("");
    setCopySourceMonth("");
    setEntryErrors({ year: "", month: "", copyYear: "", copyMonth: "" });
    setIsEntryTypeModalOpen(true);
  };

  const handleEntryTypeBack = () => {
    setEntryErrors({ year: "", month: "", copyYear: "", copyMonth: "" });
    setIsEntryTypeModalOpen(false);
  };

  const fetchPreviousCompletedEntries = useCallback(async () => {
    try {
      const response = await get(
        `/social/getSocial?type=Completed&companyId=${user?._id}`,
      );
      const payload = response?.data?.data;
      const completedRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setPreviousCompletedEntries(completedRows);
    } catch (error) {
      console.error("Error fetching previous completed social entries:", error);
      setPreviousCompletedEntries([]);
    }
  }, [user?._id]);

  useEffect(() => {
    if (!isEntryTypeModalOpen) return;
    fetchPreviousCompletedEntries();
  }, [isEntryTypeModalOpen, fetchPreviousCompletedEntries]);

  const handleEntryTypeProceed = async () => {
    const newErrors = { year: "", month: "", copyYear: "", copyMonth: "" };
    let hasErrors = false;

    if (!entryYear) {
      newErrors.year = "Year is required.";
      hasErrors = true;
    }
    if (entryType === "monthly" && !entryMonth) {
      newErrors.month = "Month is required.";
      hasErrors = true;
    }
    if (copyPreviousChoice === "yes" && !copySourceYear) {
      newErrors.copyYear = "Year is required.";
      hasErrors = true;
    }
    if (
      copyPreviousChoice === "yes" &&
      entryType === "monthly" &&
      !copySourceMonth
    ) {
      newErrors.copyMonth = "Month is required.";
      hasErrors = true;
    }
    if (copyPreviousChoice === "yes" && !selectedSourceId) {
      if (entryType === "monthly")
        newErrors.copyMonth = "Select valid previous month/year.";
      else newErrors.copyYear = "Select valid previous year.";
      hasErrors = true;
    }

    setEntryErrors(newErrors);
    if (hasErrors) return;

    setIsEntryTypeModalOpen(false);
    try {
      const payload = {
        module: entryType, // "annual" or "monthly"
        yearToSubmit: entryYear,
        copyPrevious: copyPreviousChoice === "yes",
      };

      if (entryType === "monthly") {
        payload.monthToSubmit = entryMonth;
      }
      if (copyPreviousChoice === "yes") {
        payload.sourceId = selectedSourceId;
      }
      const response = await post("/social/create", payload);
      const message = response?.data?.message;
      const status = response?.data?.statusCode;

      // If API says data already exists, show error and stop here
      if (
        status === 0 ||
        (typeof message === "string" &&
          message.toLowerCase().includes("already exists"))
      ) {
        setsnackOpen({
          open: true,
          message: message || "Data already exists",
          severity: "error",
        });
        return;
      }

      const socialDataId = response?.data?.id;
      const createdId =
        response?.data?.data?._id ||
        response?.data?.data?.id ||
        response?.data?._id;
      if (createdId) {
        setsnackOpen({
          open: true,
          message: message || "Data created successfully",
          severity: "success",
        });
        // Fetch updated table data
        const type =
          activeTab === 0 ? ["Pending", "Partially Completed"] : "Completed";
        await fetchData(type);
        // Navigate to Employee Wellbeing page with the created ID and entry type
        navigate(`/social/data/employee-wellbeing/${socialDataId}`);
      } else {
        setsnackOpen({
          open: true,
          message: "Failed to create entry. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        const errorMessages = error.response.data.message;
        if (errorMessages.includes("already exists")) {
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        } else {
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        }
      } else {
        setsnackOpen({
          open: true,
          message: error?.message || "An error occurred while saving the data.",
          severity: "error",
        });
      }
    }
  };

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <Unauthorized requiredModule="Social">
        <MessagePopup {...{ snackOpen, setsnackOpen }} />{" "}
        {/* Entry Type Selection Modal */}
        <Dialog
          open={isEntryTypeModalOpen}
          onClose={handleEntryTypeBack}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "8px",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              pb: 1,
            }}
          >
            Select Entry Type
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "#666",
                fontSize: "0.95rem",
              }}
            >
              Choose how you would like to enter the data. You can change this
              later if needed.
            </Typography>
            <RadioGroup
              value={entryType}
              onChange={(e) => {
                setEntryType(e.target.value);
                // Reset selections when switching type
                setEntryYear("");
                setEntryMonth("");
                setCopyPreviousChoice("no");
                setCopySourceYear("");
                setCopySourceMonth("");
                setEntryErrors({
                  year: "",
                  month: "",
                  copyYear: "",
                  copyMonth: "",
                });
              }}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="annual"
                control={
                  <Radio
                    sx={{
                      color: "#1c486b",
                      "&.Mui-checked": {
                        color: "#1c486b",
                      },
                    }}
                  />
                }
                label="Annual Module"
                sx={{
                  mb: 2,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "1rem",
                  },
                }}
              />
              <FormControlLabel
                value="monthly"
                control={
                  <Radio
                    sx={{
                      color: "#1c486b",
                      "&.Mui-checked": {
                        color: "#1c486b",
                      },
                    }}
                  />
                }
                label="Monthly Module"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "1rem",
                  },
                }}
              />
            </RadioGroup>

            {/* Year / Month selection based on chosen module */}
            {entryType === "annual" && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Select Year
                </Typography>
                <SelectField
                  name="Year"
                  value={entryYear}
                  handleChange={(e) => {
                    setEntryYear(e.target.value);
                    setEntryErrors((prev) => ({ ...prev, year: "" }));
                  }}
                  options={years}
                  errors={entryErrors.year}
                  defaultValue="Tap to select"
                  width="100%"
                />
              </Box>
            )}

            {entryType === "monthly" && (
              <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Select Month
                  </Typography>
                  <SelectField
                    name="Month"
                    value={entryMonth}
                    handleChange={(e) => {
                      setEntryMonth(e.target.value);
                      setEntryErrors((prev) => ({ ...prev, month: "" }));
                    }}
                    label="Month"
                    field="month"
                    errors={entryErrors.month}
                    defaultValue="Tap to select"
                    options={monthlyOptions}
                  />
                </Box>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Select Year
                  </Typography>
                  <SelectField
                    name="Year"
                    value={entryYear}
                    handleChange={(e) => {
                      setEntryYear(e.target.value);
                      setEntryErrors((prev) => ({ ...prev, year: "" }));
                      setCopySourceYear("");
                      setCopySourceMonth("");
                    }}
                    options={monthlyYearOptions}
                    errors={entryErrors.year}
                    defaultValue="Tap to select"
                  />
                </Box>
              </Box>
            )}

            {hasModulePreviousData && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Do you want to copy the previous data?
                </Typography>
                <RadioGroup
                  sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}
                  value={copyPreviousChoice}
                  onChange={(e) => {
                    setCopyPreviousChoice(e.target.value);
                    setCopySourceYear("");
                    setCopySourceMonth("");
                    setEntryErrors((prev) => ({
                      ...prev,
                      copyYear: "",
                      copyMonth: "",
                    }));
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
            )}

            {hasModulePreviousData && copyPreviousChoice === "yes" && (
              <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                {entryType === "monthly" && (
                  <Box sx={{ flex: "1 1 200px" }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Available Month
                    </Typography>
                    <SelectField
                      name="copyMonth"
                      value={copySourceMonth}
                      handleChange={(e) => {
                        setCopySourceMonth(e.target.value);
                        setEntryErrors((prev) => ({ ...prev, copyMonth: "" }));
                      }}
                      label="Month"
                      field="copyMonth"
                      errors={entryErrors.copyMonth}
                      defaultValue="Tap to select"
                      options={copyMonthOptions}
                    />
                  </Box>
                )}
                <Box sx={{ flex: "1 1 200px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Available Year
                  </Typography>
                  <SelectField
                    name="copyYear"
                    value={copySourceYear}
                    handleChange={(e) => {
                      setCopySourceYear(e.target.value);

                      setEntryErrors((prev) => ({
                        ...prev,
                        copyYear: "",
                        copyMonth: "",
                      }));
                    }}
                    options={copyYearOptions}
                    errors={entryErrors.copyYear}
                    defaultValue="Tap to select"
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
            }}
          >
            <Button
              onClick={handleEntryTypeBack}
              sx={{
                textTransform: "none",
                color: "#666",
                fontFamily: "Inter",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleEntryTypeProceed}
              variant="contained"
              disabled={!canEntryTypeProceed}
              sx={{
                backgroundColor: "#1c486b",
                textTransform: "none",
                fontFamily: "Inter",
                fontWeight: "500",
                "&:hover": {
                  backgroundColor: "#1c486b",
                  opacity: 0.9,
                },
              }}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>
        <AddSocial
          handleClose={() => {
            setIsModalOpen(false);
            fetchData("Pending");
            setActiveTab(0);
          }}
          title="Waste Generated in Operations"
          addData={isModalOpen}
          addUrl={`scope3/wasteGeneratedInOperations/add`}
          updateUrl={`scope3/wasteGeneratedInOperations/update`}
          {...{
            setsnackOpen,
          }}
        />
        <Box sx={{ paddingTop: "20px", width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            DATA
          </Typography>
          {/* <Typography variant="body2" sx={{ mb: 2 }}>
            To change how frequently you want to create data, navigate to the
            Settings.
          </Typography> */}

          {/* Tabs */}
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <Box style={{ display: "flex", gap: "1rem" }}>
              {tabLabels.map((label, index) => (
                <Button
                  key={label}
                  onClick={() => {
                    setActiveTab(index);
                    setPage(1);
                  }}
                  variant={activeTab === index ? "contained" : "outlined"}
                  sx={tabStyles(activeTab === index)}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Box>
              <Button
                variant="contained"
                endIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1C486B",
                  textTransform: "none",
                  fontFamily: "Inter",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#1C486B",
                    opacity: 0.9,
                  },
                }}
                onClick={handleAddNewClick}
              >
                Add New
              </Button>
            </Box>
          </Box>

          {/* Table or Loading */}
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              style={{ marginBottom: "1.5rem" }}
            >
              <Table sx={{ borderCollapse: "collapse" }}>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header}
                        align="left"
                        sx={{
                          borderRight: "1px solid #E0E0E0",
                          borderBottom: "1px solid #E0E0E0",
                          fontWeight: "bold",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.length === 0 ? (
                    <TableRow sx={{ width: "100%" }}>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", padding: "20px" }}
                      >
                        <NoDataFound />
                      </TableCell>
                      {/* <NoDataFound /> */}
                    </TableRow>
                  ) : (
                    data.map((row, index) => (
                      <TableRow
                        key={row._id}
                        sx={{
                          "& td": {
                            borderRight: "1px solid #E0E0E0",
                            verticalAlign: "middle",
                          },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", padding: "5px 15px" }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", padding: "5px 8px" }}
                        >
                          {getModuleDisplay(row)}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", padding: "5px 15px" }}
                        >
                          {new Date(row.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "14px",
                            color: getStatusColor(row.status),
                            padding: "5px 15px",
                          }}
                        >
                          {row.status}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", padding: "5px" }}
                        >
                          <Button
                            variant="text"
                            sx={{
                              color: "#115293",
                              textTransform: "none",

                              fontSize: "14px",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() =>
                              (() => {
                                const id = row?._socialDataRef?._id;
                                navigate(
                                  `/social/data/employee-wellbeing/${id}`,
                                );
                              })()
                            }
                          >
                            {getActionLabel(row.status)}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalPages > 1 && (
            <PaginationBox
              {...{ totalPages, page, handleChangePage }}
              selectedColor="#1C486B"
            />
          )}
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default SocialData;
